// Transitional bridge: mirror posStore (v1 cart slice) into cartStore (v2)
// and run /cart/preview against it.
//
// Goal: make /cart/preview fire on every cart change WITHOUT touching the
// dozen+ existing components that read posStore directly. Once Step 5 of
// POS_V2_PLAN_INMEMORY.md (the all-or-nothing wiring PR) lands, those
// components migrate to read cartStore directly and this bridge is deleted.
//
// What this hook does (in order, every render):
//   1. Read posStore.cart + posStore.selectedCustomer.
//   2. Project them into a `Cart` snapshot (lib/cart.ts shape).
//   3. Push the snapshot into cartStore so anything subscribing to v2 state
//      (the BroadcastChannel sender, future v2 components, the CFD) sees
//      the live cart.
//   4. Run useCartPreview against the cartStore. Preview responses land in
//      cartStore.preview / .previewStatus.
//
// What this hook does NOT do:
//   - Replace posStore. The legacy reconciliation hook (useSyncReceiptTotals)
//     keeps running until parity is verified and v2 components ship.
//   - Compute totals client-side. The whole point is to surface BE-computed
//     totals via /cart/preview; this hook is plumbing, not math.
//
// Mount this in `routes/pos/index.tsx` next to `useSyncReceiptTotals()`.

import { useEffect, useMemo, useRef } from 'react'
import { usePOSStore } from '@/stores/posStore'
import { useCartStore } from '@/stores/cartStore'
import { useCartPreview, fetchCartPreview, cartToRequest } from '@/api/cart'
import {
  cartFingerprint,
  newCartLine,
  type Cart,
  type CartCustomerDisplay,
  type CartLine,
  type PreviewTotals,
} from '@/lib/cart'
import type { Customer } from '@/types'
import { classifyPreviewError, type PreviewErrorInfo } from '@/api/v2'
import { fireBeacon } from '@/lib/beacon'
import { useSessionStore } from '@/stores/sessionStore'
import { eligibleBaseOf, isDiscountGateCode, receiptValueKey, toCents } from '@/lib/discountLimit'

/** Cadence for the self-heal preview retry while previewStatus is 'error'.
 *  See the retry effect at the bottom of useV2PreviewBridge. 4s is gentle on
 *  a struggling backend (a single register polling) yet quick enough that the
 *  cashier isn't stuck behind the Checkout gate long once the BE recovers. */
const PREVIEW_ERROR_RETRY_MS = 4_000

/** Capture a /cart/preview failure for diagnostics. UNCONDITIONAL (not
 *  DEV-gated): a till stuck on the preview-error banner is otherwise a black
 *  box — the cause (auth vs basket-rejection vs transport vs offline) is
 *  invisible without this, which is exactly why the field incident couldn't be
 *  diagnosed. The beacon is an `<img>` GET (see lib/beacon), so it lands in the
 *  server log even when the axios client is wedged behind a stuck refresh.
 *  Fired at error ONSET only; the 4s self-heal loop does not re-beacon. */
function reportPreviewError(info: PreviewErrorInfo): void {
  try {
    const { selectedStore, selectedRegister } = useSessionStore.getState()
    console.error('[cart/preview] failed', info)
    fireBeacon({
      diag: 'cart_preview_error',
      v: 1,
      kind: info.kind,
      status: info.status ?? '',
      code: info.code ?? '',
      online: typeof navigator !== 'undefined' ? navigator.onLine : true,
      store: selectedStore?.id ?? '',
      register: selectedRegister?.id ?? '',
    })
  } catch {
    /* diagnostics must never affect the cart */
  }
}

function projectCustomer(c: Customer | null): CartCustomerDisplay | null {
  if (!c) return null
  return {
    id: c.id,
    display_name: c.display_name ?? c.full_name ?? c.name ?? null,
    first_name: c.first_name ?? null,
    last_name: c.last_name ?? null,
    flowers_limit_grams: c.purchase_limit ?? c.daily_limit_grams ?? null,
    daily_purchased_grams: c.daily_purchased_grams ?? null,
  }
}

/**
 * Project the current posStore cart into a Cart shape and keep cartStore in
 * sync. Returns the latest preview status so callers (e.g. CartSummary) can
 * render a "calling backend" indicator next to the v2 totals.
 */
export function useV2PreviewBridge() {
  const cart = usePOSStore((s) => s.cart)
  const selectedCustomer = usePOSStore((s) => s.selectedCustomer)
  const isRefundMode = usePOSStore((s) => s.isRefundMode)
  const serverTotals = usePOSStore((s) => s.serverTotals)

  // Refund mode skips /cart/preview (BE rejects negative qty), so the
  // cart-side authoritative numbers live on posStore.serverTotals
  // (set by FunctionsTab/OrderDetailModal at refund start, then re-set
  // by ProductModal/CheckoutModal after every PUT/DELETE/payment).
  // The CFD reads cartStore.preview.totals over BroadcastChannel — it
  // doesn't know about posStore — so without this mirror the CFD
  // shows stale totals (or none) while the cart shows fresh ones.
  useEffect(() => {
    if (!isRefundMode || serverTotals === null) return
    const store = useCartStore.getState()
    const totals: PreviewTotals = {
      subtotal: String(serverTotals.subtotal),
      taxes: String(serverTotals.taxes),
      total: String(serverTotals.total),
      discount_amount: '0',
      deposit_amount: String(serverTotals.deposit ?? 0),
      recycling_amount: null,
      amount_to_pay: String(serverTotals.amountToPay ?? serverTotals.total),
    }
    const prev = store.preview
    useCartStore.setState({
      preview: {
        id: prev?.id ?? 0,
        number: prev?.number ?? '',
        items: prev?.items ?? [],
        warnings: prev?.warnings ?? [],
        ...prev,
        totals,
        amount_to_pay: totals.amount_to_pay,
      },
      previewedAt: Date.now(),
    })
  }, [isRefundMode, serverTotals])

  // Refund mode never carries a manager approval (refunds aren't gated).
  useEffect(() => {
    if (isRefundMode) useCartStore.getState().clearDiscountApprovals()
  }, [isRefundMode])

  // The cart moved to another BE receipt (recall/unhold rebinds it in
  // posStore.loadReceiptIntoCart): drop the previous receipt's approvals
  // (cartStore.receiptRebound keeps them when binding to the receipt this
  // cart itself just committed). Done here rather than as a store
  // subscription because cartStore and posStore import each other and may
  // not subscribe at module evaluation.
  const currentReceiptId = usePOSStore((s) => s.currentReceiptId)
  const lastReceiptIdRef = useRef(currentReceiptId)
  useEffect(() => {
    if (lastReceiptIdRef.current === currentReceiptId) return
    lastReceiptIdRef.current = currentReceiptId
    useCartStore.getState().receiptRebound(currentReceiptId ?? null)
  }, [currentReceiptId])

  // Mirror posStore.cart -> cartStore.lines on every change. The mirror is
  // one-way: changes that happen in cartStore (none, today) do not flow
  // back to posStore. This keeps the migration boundary obvious.
  useEffect(() => {
    const store = useCartStore.getState()
    const customer_id = selectedCustomer?.id ?? null

    // Build CartLines from posStore items. `lib/cart.newCartLine` mints a
    // localId each call, but we want stable ids across re-renders so the
    // animation key doesn't change unnecessarily. Use the posStore id as
    // the basis for the localId.
    //
    // Per-line discount: posStore is the source of truth. ProductModal's
    // Discount tab writes to `cart[].discount_amount` via updateCartItem;
    // we mirror it onto the corresponding CartLine here so the next
    // /cart/preview body includes `items[].discount_amount` (cartFingerprint
    // includes the discount, cartToRequest serializes it).
    const lines: CartLine[] = cart.map((item) => {
      const lotNumber = item.lot_number ?? null
      const existing = store.lines.find(
        (l) =>
          l.product_id === item.product_id
          && l.lot_number === lotNumber,
      )
      const product = item.product as (typeof item.product & {
        brand_name?: string | null
        vendor_name?: string | null
        product_unit?: string | null
        unit?: { name?: string | null } | null
        compare_price?: number | string | null
      }) | null
      const brandName =
        (typeof product?.brand_name === 'string' && product.brand_name) ||
        (typeof product?.vendor_name === 'string' && product.vendor_name) ||
        null
      const unitName =
        (typeof product?.product_unit === 'string' && product.product_unit) ||
        (typeof product?.unit?.name === 'string' && product.unit.name) ||
        null
      const compareRaw = product?.compare_price ?? null
      const comparePrice =
        compareRaw == null
          ? null
          : (typeof compareRaw === 'number' ? compareRaw : parseFloat(String(compareRaw))) || null
      const giftCardNumber = item.gift_card_number ?? null
      const giftCardBalance = giftCardNumber != null && item.gift_card_balance != null
        ? item.gift_card_balance
        : null
      const fromNew = newCartLine({
        product_id: item.product_id,
        qty: item.quantity,
        lot_number: lotNumber,
        gift_card_number: giftCardNumber,
        gift_card_balance: giftCardBalance,
        // Carry scan-to-confirm state through to cartStore so /cart/commit
        // persists it. Only present on scan-gated cannabis lines.
        scan_confirmed: item.scan_confirmed,
        display: {
          description:
            item.product?.description
            ?? item.product?.display_name
            ?? item.product?.name
            ?? '',
          image: item.product?.image ?? item.product?.image_url ?? null,
          estimated_price: item.unit_price,
          brand_name: brandName,
          unit_name: unitName,
          compare_price: comparePrice,
          on_sale: product?.on_sale === true,
        },
      })
      const itemDiscount = typeof item.discount_amount === 'number'
        ? item.discount_amount
        : parseFloat(String(item.discount_amount ?? '')) || 0
      const lineDiscount = itemDiscount > 0 ? itemDiscount : null
      // Reason metadata only rides with a live discount.
      const reasonId = lineDiscount != null ? item.discount_reason_id ?? null : null
      const reasonOther = lineDiscount != null ? item.discount_reason_other ?? null : null
      // Gift-card lines: BE has no catalog price for the gift-card SKU,
      // so /cart/preview reprices to $0 unless we send the cashier's
      // unit_price as a manual_price override. Other manual-price flows
      // (e.g. cashier price edits) can adopt the same path once they
      // wire `manual_price: true` onto the cart line.
      const manualPriceOverride =
        item.manual_price === true && Number.isFinite(item.unit_price)
          ? item.unit_price
          : null
      // Preserve the existing line's localId + priced data if it's the same
      // logical line. This keeps animations stable across re-renders.
      if (existing) {
        return {
          ...fromNew,
          localId: existing.localId,
          priced: existing.priced,
          state: existing.state,
          discount_amount: lineDiscount,
          discount_reason_id: reasonId,
          discount_reason_other: reasonOther,
          manual_price: manualPriceOverride,
        }
      }
      return {
        ...fromNew,
        discount_amount: lineDiscount,
        discount_reason_id: reasonId,
        discount_reason_other: reasonOther,
        manual_price: manualPriceOverride,
      }
    })

    // Only patch when something actually changed; useCartStore.setState would
    // trigger a re-render even on identity-equal lines arrays.
    const sameLines =
      lines.length === store.lines.length
      && lines.every((l, i) => {
        const o = store.lines[i]
        return (
          o.product_id === l.product_id
          && o.qty === l.qty
          && o.lot_number === l.lot_number
          && (o.discount_amount ?? null) === (l.discount_amount ?? null)
          && (o.manual_price ?? null) === (l.manual_price ?? null)
          // Confirming a scan flips only scan_confirmed; without this the
          // bridge would treat it as "no change" and never propagate the
          // confirmed flag to cartStore, so /cart/commit wouldn't persist it.
          && (o.scan_confirmed ?? undefined) === (l.scan_confirmed ?? undefined)
          && (o.discount_reason_id ?? null) === (l.discount_reason_id ?? null)
          && (o.discount_reason_other ?? null) === (l.discount_reason_other ?? null)
        )
      })
    const sameCustomer = store.customer_id === customer_id

    if (sameLines && sameCustomer) {
      return
    }
    useCartStore.setState({
      lines,
      customer_id,
      // Project the full Customer down to the CFD-friendly display shape.
      // The CFD doesn't need every field -- name + cannabis-limit data is
      // enough to render the v1 layout. Updates flow over BroadcastChannel
      // automatically because `setupPosCartBroadcast` subscribes to every
      // cartStore change.
      customer: projectCustomer(selectedCustomer),
      // Keep the prior `preview` so totals don't blank between mutations.
      // CartSummary uses it to extrapolate an instant tax estimate via the
      // tax/subtotal ratio. Only an empty cart explicitly clears preview.
      preview: lines.length === 0 ? null : store.preview,
      previewStatus: lines.length === 0 ? 'idle' : 'stale',
    })
    // Manager approvals: a new customer invalidates all of them (their
    // discounts are cleared on a group change anyway). A line change drops
    // the approvals whose bound tuple left the cart (qty +/-, lot swap, a
    // base change under a receipt discount); metadata-only changes (reason,
    // scan_confirmed) aren't in the tuple and keep them. An approval recorded
    // a moment ago and not yet applied survives the grace period, so a
    // multi-step posStore write mirroring an intermediate shape can't drop it.
    if (!sameCustomer) useCartStore.getState().clearDiscountApprovals()
    else useCartStore.getState().pruneDiscountApprovals()
  }, [cart, selectedCustomer])

  // Watch cartStore directly so useCartPreview observes the snapshot we just
  // wrote. We MUST select primitives (or stable refs) here -- returning a
  // synthesized object from a single zustand selector breaks the
  // useSyncExternalStore caching invariant ("getSnapshot should be cached
  // to avoid an infinite loop") and triggers a render storm.
  //
  // Subscribe ONLY to fields useCartPreview actually needs to decide
  // whether to fire (lines, customer_id, exempt_taxes, discount -- the
  // cartFingerprint inputs -- plus customer for projection consistency).
  // previewStatus/previewedAt/preview/partial* are written
  // BY the preview cycle itself; subscribing to them here would cause the
  // v2Cart memo to recompute when onPending flips status 'stale' ->
  // 'fetching', which would re-run useCartPreview's effect mid-debounce
  // and cause it to cancel + reschedule (or, before the abort-signal fix,
  // fire a duplicate request).
  const customerIdSel = useCartStore((s) => s.customer_id)
  const customerSel = useCartStore((s) => s.customer)
  const exemptTaxesSel = useCartStore((s) => s.exempt_taxes)
  const linesSel = useCartStore((s) => s.lines)
  const discountSel = useCartStore((s) => s.discount)

  const v2Cart: Cart = useMemo(
    () => {
      // The fields not subscribed to above are read lazily from the store
      // so the Cart projection stays type-complete without forcing a
      // re-render when they change. They aren't used by useCartPreview's
      // fingerprint/isEmpty/cartToRequest path.
      const s = useCartStore.getState()
      return {
        customer_id: customerIdSel,
        customer: customerSel,
        exempt_taxes: exemptTaxesSel,
        lines: linesSel,
        preview: s.preview,
        previewStatus: s.previewStatus,
        previewedAt: s.previewedAt,
        partialPayments: s.partialPayments,
        partialAmountToPay: s.partialAmountToPay,
        discount: discountSel,
        loyalty: s.loyalty,
      }
    },
    [
      customerIdSel,
      customerSel,
      exemptTaxesSel,
      linesSel,
      discountSel,
    ],
  )

  // Refund carts carry negative qty per line; /cart/preview rejects them with
  // "qty must be greater than zero". Refund pricing comes from the source
  // receipt + /receipts/copy-with-refund flow, so skip the preview entirely
  // and let CartSummary fall back to the local-estimate tier.
  useCartPreview(
    v2Cart,
    {
      onPending: () => useCartStore.getState().setPreviewPending(''),
      onPreview: (preview, fingerprint) => {
        // The store re-derives the current fingerprint internally to drop
        // stale resolutions. We pass `fingerprint` as both fired + current
        // here because the store's only consumer is this bridge; if the
        // posStore mirror has moved on, the next mirror+preview cycle will
        // overwrite this one anyway.
        const store = useCartStore.getState()
        store.applyPreview(preview, fingerprint, fingerprint)
        // The priced shape is the live one now: drop approvals whose tuple
        // left the cart, and a receipt approval whose base the server now
        // prices differently (it would refuse to reuse it). A preview for
        // the pre-approval cart landing right after an approval was recorded
        // can't drop it (pruneApprovals' grace).
        const now = useCartStore.getState()
        if (cartFingerprint(now) === fingerprint) {
          const base = receiptValueKey(now.discount) != null
            ? eligibleBaseOf(preview.totals, true)
            : null
          store.pruneDiscountApprovals({ eligibleBaseCents: base != null ? toCents(base) : null })
        }
      },
      takePrimed: (fingerprint) => useCartStore.getState().takePrimedPreview(fingerprint),
      onError: (err) => {
        // Onset of a preview failure. Classify so the UI can message
        // truthfully (and so the self-heal loop's recovery semantics are
        // visible in the log), then beacon once.
        const info = classifyPreviewError(err)
        // A refused approval (override_refused / override_limit_exceeded)
        // or a throttled PIN: stop sending it. A 409 discount_exceeds_limit
        // while approvals ride: drop the one for the refused tuple. The
        // banner re-prompts.
        useCartStore.getState().handleGateError(info.code, info.extra)
        useCartStore.getState().setPreviewError(info)
        reportPreviewError(info)
      },
    },
    !isRefundMode,
  )

  // State-level watchdog. useCartPreview has its OWN fetch-lifecycle
  // watchdog (api/cart.ts: PREVIEW_WATCHDOG_MS) that calls onError after
  // 10s of unresolved fetch. This is the belt-and-suspenders that catches
  // any path where previewStatus goes 'fetching' but never resolves --
  // including races where setPreviewPending fires from a code path that
  // bypasses useCartPreview's fetch chain entirely (e.g. setPreviewStatus
  // called directly from another effect, future code that flips status
  // without arming the fetch, HMR module-replacement leaving an orphan
  // 'fetching' state). Resets to 'error' so CartSummary surfaces the
  // amber "Could not refresh prices" banner and Checkout re-enables.
  //
  // Subscribe via getState() inside the effect so we only re-arm when
  // previewStatus actually changes (selector subscription would also
  // work but adds a render path; this stays out of the render hot path).
  const previewStatus = useCartStore((s) => s.previewStatus)
  useEffect(() => {
    if (previewStatus !== 'fetching') return
    const timer = window.setTimeout(() => {
      const current = useCartStore.getState().previewStatus
      if (current === 'fetching') {
        // Still stuck after the grace window -- treat as a hung fetch.
        // Hard-coded 11s (1s longer than api/cart.ts's PREVIEW_WATCHDOG_MS)
        // so the fetch-level watchdog fires first under normal conditions;
        // this only catches the leak case. A hang is a transient transport
        // stall, so tag it as such (no thrown error to classify here).
        useCartStore.getState().setPreviewError({
          kind: 'transient',
          status: null,
          code: null,
          message: 'preview watchdog timeout',
        })
      }
    }, 11_000)
    return () => window.clearTimeout(timer)
  }, [previewStatus])

  // Self-heal: while a sale-mode preview is errored, re-attempt the SAME
  // shape on a fixed cadence so a transient BE/network blip recovers WITHOUT
  // a cashier mutation. useCartPreview only re-fires on a shape change, so
  // without this the cart sticks on 'error' — and, with the Checkout gate in
  // CartSummary, the Pay button stays disabled — until the cashier pokes a
  // line. The first success flips status back to 'idle' (Tier 1) and
  // re-enables Checkout.
  //
  // Retries continue for EVERY error kind, including a deterministic 'rejected'
  // (a 4xx the BE will keep refusing for this exact basket). That's deliberate:
  // the cashier-facing copy already tells them to fix the cart, and a cart edit
  // tears this loop down and re-prices the new shape — but leaving the loop
  // running also lets a 'rejected' self-heal if the BE-side cause clears
  // externally (restock, flag flip, catalog fix) without any cashier action,
  // which matches the "recover without intervention" goal. The cost is one
  // gentle read every 4s on an idle stuck cart; the dedupe in
  // cartStore.setPreviewError keeps that from re-rendering anything.
  //
  // Lives here, not inside useCartPreview, to leave that delicate
  // fetch-lifecycle hook untouched; the bridge already owns direct preview
  // writes (the refund mirror above) and the state-level watchdog. The loop
  // self-reschedules from inside one effect run rather than leaning on a
  // status dep change: re-setting 'error' on top of 'error' is a no-op that
  // wouldn't re-trigger the effect, and flipping to 'fetching' mid-attempt
  // would tear this effect down (it keys on previewStatus). So status is left
  // at 'error' across attempts; a genuine cart mutation flips it to 'stale',
  // which tears this effect down and hands the new shape to useCartPreview.
  //
  // EXCEPT the discount-limit gate (409 discount_exceeds_limit and the
  // override_* 409s): retrying the same basket can only fail the same way
  // until a manager approves or the cashier removes the discount, so the loop
  // stays off and the banner offers exactly those two actions.
  const previewErrorCode = useCartStore((s) => s.previewError?.code ?? null)
  useEffect(() => {
    if (isRefundMode || previewStatus !== 'error') return
    if (isDiscountGateCode(previewErrorCode)) return

    let stopped = false
    let timer = 0
    let ctrl: AbortController | null = null

    const attempt = async () => {
      if (stopped) return
      // Price the LIVE store shape so a mutation that landed between
      // scheduling and firing is reflected (and caught at apply time).
      const snapshot = useCartStore.getState()
      if (snapshot.lines.length === 0) return
      const firedFingerprint = cartFingerprint(snapshot)
      ctrl = new AbortController()
      // Bound a hung transport so the loop can't stall (mirrors
      // useCartPreview's PREVIEW_WATCHDOG_MS).
      const watchdog = window.setTimeout(() => ctrl?.abort(), 10_000)
      try {
        const fresh = await fetchCartPreview(cartToRequest(snapshot), ctrl.signal)
        window.clearTimeout(watchdog)
        if (stopped) return
        // applyPreview drops the response when the shape moved on
        // (fired != current); on a match it sets status 'idle', whereupon
        // this effect's previewStatus dep changes and it tears down.
        const current = cartFingerprint(useCartStore.getState())
        useCartStore.getState().applyPreview(fresh, firedFingerprint, current)
        // Still errored (the response was dropped as stale) — keep trying.
        if (!stopped && useCartStore.getState().previewStatus === 'error') {
          timer = window.setTimeout(attempt, PREVIEW_ERROR_RETRY_MS)
        }
      } catch (err) {
        window.clearTimeout(watchdog)
        if (stopped) return
        // Refresh the classified reason — the failure mode can shift between
        // attempts (e.g. transient -> a hard 4xx once a mutation lands). The
        // onset beacon already fired via onError; we don't re-beacon every 4s.
        const info = classifyPreviewError(err)
        useCartStore.getState().handleGateError(info.code, info.extra)
        useCartStore.getState().setPreviewError(info)
        // A gate code re-runs this effect (previewErrorCode dep), which
        // then stays off; don't queue another attempt from here.
        if (isDiscountGateCode(info.code)) return
        timer = window.setTimeout(attempt, PREVIEW_ERROR_RETRY_MS)
      }
    }

    timer = window.setTimeout(attempt, PREVIEW_ERROR_RETRY_MS)
    return () => {
      stopped = true
      window.clearTimeout(timer)
      if (ctrl) ctrl.abort()
    }
  }, [previewStatus, isRefundMode, previewErrorCode])
}
